import React, { useState } from 'react';
import { PlusIcon, Edit2Icon, Trash2Icon, TargetIcon } from 'lucide-react';
import type { Goal } from '../../types';
import { Header } from '../layout/Header';
import { GoalForm } from './GoalForm';
import { getGoals } from '../../data/mockData';
import { formatMetricLabel } from '../../utils/calculations';
function formatAssignment(goal: Goal): string {
  if (goal.assignmentLevel === 'company') return 'Company-Wide';
  const labels: Record<string, string> = {
    division: 'Division',
    team: 'Team',
    loanOfficer: 'Loan Officer'
  };
  return `${labels[goal.assignmentLevel]}: ${goal.assignmentId ?? '—'}`;
}
export function GoalConfigPage() {
  const [goals, setGoals] = useState<Goal[]>(getGoals());
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | undefined>(undefined);
  const handleSave = (goal: Goal) => {
    setGoals(prev => {
      const idx = prev.findIndex(g => g.id === goal.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = goal;
        return updated;
      }
      return [...prev, goal];
    });
    setShowForm(false);
    setEditingGoal(undefined);
  };
  const handleEdit = (goal: Goal) => {
    setEditingGoal(goal);
    setShowForm(true);
  };
  const handleDelete = (goalId: string) => {
    setGoals(prev => prev.filter(g => g.id !== goalId));
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingGoal(undefined);
  };
  return <div className="min-h-screen" data-id="element-94">
      <Header title="Configure Goals" subtitle="Set performance targets for Soft Pull and Submitted metrics" data-id="element-95">
        {!showForm && <button onClick={() => {
        setEditingGoal(undefined);
        setShowForm(true);
      }} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm" aria-label="Add new goal" data-id="element-96">
            <PlusIcon className="w-4 h-4" data-id="element-97" />
            Add Goal
          </button>}
      </Header>

      <div className="p-6 lg:p-8 space-y-6" data-id="element-98">
        {/* Form */}
        {showForm && <GoalForm goal={editingGoal} onSave={handleSave} onCancel={handleCancel} data-id="element-99" />}

        {/* Goals Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-100">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-101">
            <TargetIcon className="w-5 h-5 text-gray-400" data-id="element-102" />
            <h3 className="text-base font-semibold text-gray-900" data-id="element-103">
              Current Goals
            </h3>
            <span className="text-sm text-gray-400 ml-1" data-id="element-104">({goals.length})</span>
          </div>

          {goals.length === 0 ? <div className="px-6 py-12 text-center" data-id="element-105">
              <TargetIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-106" />
              <p className="text-sm text-gray-500" data-id="element-107">
                No goals configured yet. Click "Add Goal" to get started.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-108">
              <table className="w-full text-sm" aria-label="Goals configuration table" data-id="element-109">
                <thead data-id="element-110">
                  <tr className="bg-gray-50 border-b border-gray-100" data-id="element-111">
                    <th className="text-left px-6 py-3 font-medium text-gray-500 uppercase text-xs tracking-wider" data-id="element-112">
                      Metric
                    </th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500 uppercase text-xs tracking-wider" data-id="element-113">
                      Timeframe
                    </th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500 uppercase text-xs tracking-wider" data-id="element-114">
                      Target
                    </th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500 uppercase text-xs tracking-wider" data-id="element-115">
                      Assignment
                    </th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500 uppercase text-xs tracking-wider" data-id="element-116">
                      Effective
                    </th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500 uppercase text-xs tracking-wider" data-id="element-117">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-118">
                  {goals.map(goal => <tr key={goal.id} className="hover:bg-gray-50 transition-colors" data-id="element-119">
                      <td className="px-6 py-3.5 font-medium text-gray-900" data-id="element-120">
                        {formatMetricLabel(goal.metric)}
                      </td>
                      <td className="px-6 py-3.5 text-gray-600 capitalize" data-id="element-121">
                        {goal.timeframe}
                      </td>
                      <td className="px-6 py-3.5 text-right font-semibold text-gray-900" data-id="element-122">
                        {goal.target}
                      </td>
                      <td className="px-6 py-3.5 text-gray-600" data-id="element-123">
                        {formatAssignment(goal)}
                      </td>
                      <td className="px-6 py-3.5 text-gray-500" data-id="element-124">
                        {goal.effectiveDate}
                      </td>
                      <td className="px-6 py-3.5 text-right" data-id="element-125">
                        <div className="inline-flex items-center gap-1" data-id="element-126">
                          <button onClick={() => handleEdit(goal)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" aria-label={`Edit ${formatMetricLabel(goal.metric)} ${goal.timeframe} goal`} data-id="element-127">
                            <Edit2Icon className="w-4 h-4" data-id="element-128" />
                          </button>
                          <button onClick={() => handleDelete(goal.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" aria-label={`Delete ${formatMetricLabel(goal.metric)} ${goal.timeframe} goal`} data-id="element-129">
                            <Trash2Icon className="w-4 h-4" data-id="element-130" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}